import { useRef } from "react";

import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { hideToast, showToast } from "app/actions/toast_actions";
import type { IToast } from "app/components/React/Toast/types";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import { exportAppsToExcelService } from "app/pages/sales-intelligence/pages/find-apps/services/export/appsExcelExportService";
import { createExcelExportRequestDto } from "app/pages/sales-intelligence/pages/find-apps/utils/dto";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import type { AppsSourceType } from "app/pages/sales-intelligence/store/find-apps/types/appsSourceType";
import type { AppsSearchRequestDto } from "app/pages/sales-intelligence/store/find-apps/types/dto";
import { exportToExcelToastThunk } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/effects";

const useExcelExportMutation = (appSourceType: AppsSourceType, searchRquestDto: AppsSearchRequestDto) => {
    const dispatch = useDispatch();
    const toastRef = useRef<IToast>(null);

    return useMutation({
        mutationFn(data: { apps: readonly string[] } | { count: number }) {
            return salesApiService.findApps[appSourceType].exportToExcel(
                createExcelExportRequestDto(searchRquestDto, data),
            );
        },
        onMutate(data: { apps: readonly string[] } | { count: number }) {
            const count = "count" in data ? data.count : data.apps.length;

            toastRef.current = exportAppsToExcelService.createToast(
                exportAppsToExcelService.getExportingToastTitle(count),
            );

            dispatch(
                showToast(
                    exportAppsToExcelService.getToastComponent(toastRef.current.children as string),
                    "success",
                    toastRef.current.timestamp,
                ),
            );
        },
        async onSettled(data) {
            if (toastRef.current) {
                dispatch(hideToast(toastRef.current));
            }

            if (data?.success) {
                dispatch(exportToExcelToastThunk(exportAppsToExcelService.getSuccessToastTitle()));

                await quotaQueryClient.invalidateExportQuota();
            } else {
                dispatch(exportToExcelToastThunk(exportAppsToExcelService.getErrorToastTitle()));
            }
        },
    });
};

export default useExcelExportMutation;
